/**
 * fuzzyService.js
 * Fuzzy string matching for product name suggestions.
 * Modular — swap with a server-side vector search independently.
 */

function normalize(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // remove accents
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function bigrams(s) {
  const r = new Set();
  for (let i = 0; i < s.length - 1; i++) r.add(s.slice(i, i + 2));
  return r;
}

// Jaccard similarity on bigrams
function jaccardSim(a, b) {
  const sa = bigrams(a), sb = bigrams(b);
  if (sa.size === 0 && sb.size === 0) return 1;
  const inter = [...sa].filter(x => sb.has(x)).length;
  const union = new Set([...sa, ...sb]).size;
  return union === 0 ? 0 : inter / union;
}

// Token overlap (word-level)
function tokenOverlap(a, b) {
  const ta = new Set(a.split(" ").filter(Boolean));
  const tb = new Set(b.split(" ").filter(Boolean));
  if (ta.size === 0 || tb.size === 0) return 0;
  const inter = [...ta].filter(x => tb.has(x)).length;
  return inter / Math.max(ta.size, tb.size);
}

// Combined score
export function similarity(a, b) {
  const na = normalize(a), nb = normalize(b);
  if (!na || !nb) return 0;
  const j = jaccardSim(na, nb);
  const t = tokenOverlap(na, nb);
  return j * 0.6 + t * 0.4;
}

// Return top-N matches for a query against a list of items
export function findTopMatches(query, items, { key = "name", topN = 5, minScore = 0.15 } = {}) {
  return items
    .map(item => ({ item, score: similarity(query, item[key] || "") }))
    .filter(({ score }) => score >= minScore)
    .sort((a, b) => b.score - a.score)
    .slice(0, topN);
}

// Auto-match a list of invoice items against an ingredient catalog
export function autoMatch(invoiceItems, ingredients, minConfidence = 35) {
  return invoiceItems.map(item => {
    const matches = findTopMatches(item.description, ingredients, { minScore: 0 });
    const best = matches[0];
    const confidence = best ? Math.round(best.score * 100) : 0;
    return {
      item,
      matched: confidence >= minConfidence ? best.item : null,
      confidence,
      ingredientId: confidence >= minConfidence ? best?.item?.id : null,
      alternatives: matches.slice(1, 4).map(m => ({ ingredient: m.item, confidence: Math.round(m.score * 100) })),
      skip: false,
    };
  });
}